// TelcoTrack — sestavení maker pro n8n workflow „Nástupy“.

import {
  HR_CITY_LABELS,
  HR_CITY_LOCATIVE,
  HR_COMPANY_BY_ID,
  hrEmployerSignatoryForCompany,
  HR_HEALTH_INSURER_BY_CODE,
  hrCitiesForCompany,
  isHrCityId,
  isHrCompanyId,
  type HrCityId,
  type HrCompanyId,
  type HrNastupyMacroKey,
} from '../constants/hr-onboarding.constants';

export interface HrNastupyRawInput {
  firstName: string;
  lastName: string;
  email: string;
  companyId: HrCompanyId;
  cityId: HrCityId;
  /** Trvalé bydliště zaměstnance. */
  address: string;
  /** Datum nástupu (YYYY-MM-DD nebo DD.MM.YYYY). */
  nastup: string;
  rodneCislo: string;
  mistoNarozeni?: string;
  cisloOp?: string;
  telefon?: string;
  ucet?: string;
  druhPrace?: string;
  /** Kód pojišťovny (111…) nebo volný text. */
  pojistovna?: string;
  /** Mzda v Kč (číslo nebo řetězec s mezerami). */
  mzda?: string | number;
  /** Volitelný override data narození (jinak z RČ). */
  narozeniny?: string;
}

export type HrNastupyMacros = Record<HrNastupyMacroKey, string>;

export interface HrNastupyWebhookPayload {
  folderName: string;
  companyId: HrCompanyId;
  cityId: HrCityId;
  macros: HrNastupyMacros;
  /** Raw fields for n8n Code node (pokud zůstane transformace v n8n). */
  raw: {
    firstName: string;
    lastName: string;
    email: string;
    address: string;
    nastup: string;
    rodneCislo: string;
    mistoNarozeni: string;
    cisloOp: string;
    telefon: string;
    ucet: string;
    druhPrace: string;
    pojistovna: string;
    mzda: string;
    narozeniny: string;
  };
}

const ONES = [
  '',
  'jedna',
  'dva',
  'tři',
  'čtyři',
  'pět',
  'šest',
  'sedm',
  'osm',
  'devět',
];
const TEENS = [
  'deset',
  'jedenáct',
  'dvanáct',
  'třináct',
  'čtrnáct',
  'patnáct',
  'šestnáct',
  'sedmnáct',
  'osmnáct',
  'devatenáct',
];
const TENS = [
  '',
  '',
  'dvacet',
  'třicet',
  'čtyřicet',
  'padesát',
  'šedesát',
  'sedmdesát',
  'osmdesát',
  'devadesát',
];
const HUNDREDS = [
  '',
  'sto',
  'dvě stě',
  'tři sta',
  'čtyři sta',
  'pět set',
  'šest set',
  'sedm set',
  'osm set',
  'devět set',
];

function trimStr(value: unknown): string {
  return String(value ?? '').trim();
}

/** Normalizace RČ na `YYMMDD/XXXX` (nebo bez lomítka pokud < 10 číslic). */
export function normalizeRodneCislo(raw: string): string {
  const digits = String(raw ?? '').replace(/\D/g, '');
  if (digits.length === 9) return digits;
  if (digits.length >= 10) return `${digits.slice(0, 6)}/${digits.slice(6, 10)}`;
  return digits;
}

/**
 * Datum narození z RČ → DD.MM.YYYY.
 * Podporuje ženské +50 / +70 a výjimečné +20 / +70 u měsíce.
 */
export function birthDateFromRodneCislo(raw: string): string | null {
  const digits = String(raw ?? '').replace(/\D/g, '');
  if (digits.length < 9) return null;

  let yy = Number(digits.slice(0, 2));
  let mm = Number(digits.slice(2, 4));
  const dd = Number(digits.slice(4, 6));
  if (!Number.isFinite(yy) || !Number.isFinite(mm) || !Number.isFinite(dd)) return null;

  if (mm > 70) mm -= 70;
  else if (mm > 50) mm -= 50;
  else if (mm > 20) mm -= 20;

  if (mm < 1 || mm > 12 || dd < 1 || dd > 31) return null;

  let year = 1900 + yy;
  if (digits.length === 10 && year <= 1953) year += 100;

  const pad = (n: number) => String(n).padStart(2, '0');
  return `${pad(dd)}.${pad(mm)}.${year}`;
}

/** YYYY-MM-DD nebo DD.MM.YYYY → DD.MM.YYYY. */
export function formatHrCzechDate(raw: string): string {
  const t = trimStr(raw);
  const iso = /^(\d{4})-(\d{2})-(\d{2})$/.exec(t);
  if (iso) return `${iso[3]}.${iso[2]}.${iso[1]}`;
  const cz = /^(\d{1,2})\.(\d{1,2})\.(\d{4})$/.exec(t);
  if (cz) {
    return `${cz[1].padStart(2, '0')}.${cz[2].padStart(2, '0')}.${cz[3]}`;
  }
  return t;
}

export function parseMzdaNumber(raw: string | number | undefined | null): number | null {
  if (raw == null || raw === '') return null;
  if (typeof raw === 'number') return Number.isFinite(raw) ? Math.round(raw) : null;
  const cleaned = String(raw).replace(/\s/g, '').replace(',', '.').replace(/[^\d.-]/g, '');
  const n = Number(cleaned);
  return Number.isFinite(n) ? Math.round(n) : null;
}

function underThousand(n: number, feminineOne = false): string {
  if (n === 0) return '';
  const h = Math.floor(n / 100);
  const rest = n % 100;
  const parts: string[] = [];
  if (h) parts.push(HUNDREDS[h]);
  if (rest >= 10 && rest < 20) {
    parts.push(TEENS[rest - 10]);
  } else {
    const t = Math.floor(rest / 10);
    const o = rest % 10;
    if (t) parts.push(TENS[t]);
    if (o) {
      if (o === 1 && feminineOne) parts.push('jedna');
      else if (o === 2 && feminineOne) parts.push('dvě');
      else parts.push(ONES[o]);
    }
  }
  return parts.join(' ');
}

function thousandWord(n: number): string {
  if (n === 1) return 'tisíc';
  if (n >= 2 && n <= 4) return 'tisíce';
  return 'tisíc';
}

function millionWord(n: number): string {
  if (n === 1) return 'milion';
  if (n >= 2 && n <= 4) return 'miliony';
  return 'milionů';
}

/** Celá částka v Kč slovy (bez „korun…“ — přidej `czechCurrencyWords`). */
export function czechIntegerWords(amount: number): string {
  const n = Math.abs(Math.trunc(amount));
  if (n === 0) return 'nula';

  const millions = Math.floor(n / 1_000_000);
  const thousands = Math.floor((n % 1_000_000) / 1000);
  const rest = n % 1000;
  const parts: string[] = [];

  if (millions) {
    if (millions === 1) parts.push('jeden', millionWord(1));
    else parts.push(underThousand(millions), millionWord(millions));
  }
  if (thousands) {
    if (thousands === 1) parts.push('tisíc');
    else parts.push(underThousand(thousands), thousandWord(thousands));
  }
  if (rest) parts.push(underThousand(rest));

  return parts.filter(Boolean).join(' ').replace(/\s+/g, ' ').trim();
}

export function czechCurrencyWords(amount: number): string {
  const abs = Math.abs(Math.trunc(amount));
  if (abs === 1) return 'jedna koruna česká';
  const words = czechIntegerWords(amount);
  const mod100 = abs % 100;
  const mod10 = abs % 10;
  let unit = 'korun českých';
  if (mod100 < 10 || mod100 > 20) {
    if (mod10 >= 2 && mod10 <= 4) unit = 'koruny české';
  }
  // 2–4: „dvě/tři/čtyři koruny“ — u 2 použij ženský tvar
  if (abs === 2) return `dvě ${unit}`;
  return `${words} ${unit}`;
}

export function formatMzdaDisplay(amount: number): string {
  const formatted = new Intl.NumberFormat('cs-CZ', { maximumFractionDigits: 0 }).format(amount);
  return `${formatted} Kč`;
}

export function resolvePojistovnaLabel(raw: string): string {
  const t = trimStr(raw);
  if (!t) return '';
  const digits = t.replace(/\D/g, '');
  if (digits && HR_HEALTH_INSURER_BY_CODE[digits]) return HR_HEALTH_INSURER_BY_CODE[digits];
  const byLabel = Object.values(HR_HEALTH_INSURER_BY_CODE).find(
    (label) => label.toLowerCase() === t.toLowerCase(),
  );
  return byLabel ?? t;
}

export function validateHrCityForCompany(
  companyId: HrCompanyId,
  cityId: HrCityId,
): boolean {
  return hrCitiesForCompany(companyId).includes(cityId);
}

export function buildHrNastupyMacros(input: HrNastupyRawInput): HrNastupyMacros {
  if (!isHrCompanyId(input.companyId)) {
    throw new Error('Neplatná firma pro smlouvy.');
  }
  if (!isHrCityId(input.cityId) || !validateHrCityForCompany(input.companyId, input.cityId)) {
    throw new Error('Neplatné provozní město pro zvolenou firmu.');
  }

  const company = HR_COMPANY_BY_ID[input.companyId];
  const signatory = hrEmployerSignatoryForCompany(input.companyId);
  const jmeno = `${trimStr(input.firstName)} ${trimStr(input.lastName)}`.trim();
  const rodne = normalizeRodneCislo(input.rodneCislo);
  const narozeniny =
    formatHrCzechDate(trimStr(input.narozeniny || '')) ||
    birthDateFromRodneCislo(rodne) ||
    '';
  const mzdaNum = parseMzdaNumber(input.mzda);
  const mzdaCislo =
    mzdaNum != null
      ? new Intl.NumberFormat('cs-CZ', { maximumFractionDigits: 0 }).format(mzdaNum)
      : '';
  const mzdaSlovy = mzdaNum != null ? czechCurrencyWords(mzdaNum) : '';
  const mzdaSlovyCap =
    mzdaSlovy.length > 0 ? mzdaSlovy.charAt(0).toUpperCase() + mzdaSlovy.slice(1) : '';
  // Stejný tvar jako v Drive mustrech: „22 400 Kč (slovy: Dvacet …)“
  const mzdaDisplay =
    mzdaNum != null ? `${formatMzdaDisplay(mzdaNum)} (slovy: ${mzdaSlovyCap})` : '';

  return {
    JMENO: jmeno,
    ADRESA: trimStr(input.address),
    NASTUP: formatHrCzechDate(input.nastup),
    MISTO_NAROZENI: trimStr(input.mistoNarozeni),
    RODNE_CISLO: rodne,
    CISLO_OP: trimStr(input.cisloOp),
    TELEFON: trimStr(input.telefon),
    EMAIL: trimStr(input.email).toLowerCase(),
    UCET: trimStr(input.ucet),
    DRUH_PRACE: trimStr(input.druhPrace),
    NAROZENINY: narozeniny,
    POJISTOVNA: resolvePojistovnaLabel(trimStr(input.pojistovna)),
    MZDA: mzdaDisplay,
    MZDA_CISLO: mzdaCislo,
    MZDA_SLOVY: mzdaSlovyCap,
    FIRMA: company.name,
    ICO: company.ico,
    ADRESA_FIRMY: company.address,
    MESTO: HR_CITY_LABELS[input.cityId],
    MESTO_V: HR_CITY_LOCATIVE[input.cityId],
    ZASTUPCE: signatory.name,
    ZASTUPCE_TELEFON: signatory.phone,
    ZASTUPCE_EMAIL: signatory.email,
  };
}

export function buildHrNastupyWebhookPayload(input: HrNastupyRawInput): HrNastupyWebhookPayload {
  const macros = buildHrNastupyMacros(input);
  return {
    folderName: macros.JMENO,
    companyId: input.companyId,
    cityId: input.cityId,
    macros,
    raw: {
      firstName: trimStr(input.firstName),
      lastName: trimStr(input.lastName),
      email: trimStr(input.email).toLowerCase(),
      address: trimStr(input.address),
      nastup: trimStr(input.nastup),
      rodneCislo: trimStr(input.rodneCislo),
      mistoNarozeni: trimStr(input.mistoNarozeni),
      cisloOp: trimStr(input.cisloOp),
      telefon: trimStr(input.telefon),
      ucet: trimStr(input.ucet),
      druhPrace: trimStr(input.druhPrace),
      pojistovna: trimStr(input.pojistovna),
      mzda: trimStr(input.mzda),
      narozeniny: trimStr(input.narozeniny),
    },
  };
}

/** Klíčová pole pro spuštění generace smluv. */
export function missingHrNastupyRequiredFields(input: Partial<HrNastupyRawInput>): string[] {
  const missing: string[] = [];
  if (!trimStr(input.firstName)) missing.push('jméno');
  if (!trimStr(input.lastName)) missing.push('příjmení');
  if (!trimStr(input.email)) missing.push('email');
  if (!isHrCompanyId(input.companyId)) missing.push('firma');
  if (!isHrCityId(input.cityId)) missing.push('provozní město');
  else if (isHrCompanyId(input.companyId) && !validateHrCityForCompany(input.companyId, input.cityId)) {
    missing.push('provozní město (neplatné pro firmu)');
  }
  if (!trimStr(input.address)) missing.push('adresa');
  if (!trimStr(input.nastup)) missing.push('datum nástupu');
  if (!trimStr(input.rodneCislo)) missing.push('rodné číslo');
  if (!trimStr(input.druhPrace)) missing.push('druh práce');
  if (parseMzdaNumber(input.mzda) == null) missing.push('mzda');
  return missing;
}
